import React, { useState, useEffect, useMemo } from 'react';
import api, { API_URL } from '../services/api';
import toast from 'react-hot-toast';

/* ── USER ENTITLEMENT BOWTIE DIAGRAM COMPONENT (WITH INTERACTIVE DRILLDOWN) ── */
function UserBowTieDiagram({ user, summary, folders = [], files = [], loadingAccess = false, onDrillDown }) {
  if (loadingAccess) {
    return (
      <div style={{ background: '#FFFFFF', border: '1.5px solid #E2E8F0', borderRadius: '20px', padding: '60px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '380px' }}>
        <div className="spinner" style={{ margin: '0 auto 16px', width: '38px', height: '38px', borderWidth: '3px' }} />
        <div style={{ fontSize: '15px', color: '#64748B', fontWeight: '700' }}>Resolving user bowtie vectors and access paths...</div>
      </div>
    );
  }

  // 1. Left Wing: Access Sources & Inward Grants
  const leftSources = [
    {
      id: 'src-personal-onedrive',
      title: 'Personal OneDrive',
      subtitle: `${summary.ownedFiles?.toLocaleString() || 0} Owned Files`,
      desc: 'Direct User Storage',
      icon: '👑',
      color: '#10B981',
      bg: '#ECFDF5',
      border: '#A7F3D0',
      action: () => onDrillDown({ tab: 'files', filterType: 'OWNER' }),
    },
    {
      id: 'src-shared-folders',
      title: 'Colleague Shared Folders',
      subtitle: `${folders.filter(f => f.accessType?.includes('Shared')).length} Shared Libraries`,
      desc: 'Inward Directory Grants',
      icon: '👥',
      color: '#3B82F6',
      bg: '#EFF6FF',
      border: '#BFDBFE',
      action: () => onDrillDown({ tab: 'folders', filterType: 'SHARED' }),
    },
    {
      id: 'src-dept-membership',
      title: 'Departmental Membership',
      subtitle: user?.department || 'Corporate Member',
      desc: 'M365 Tenant Group',
      icon: '🏢',
      color: '#6366F1',
      bg: '#EEF2FF',
      border: '#C7D2FE',
      action: () => onDrillDown({ tab: 'folders', filterType: 'ALL' }),
    },
    {
      id: 'src-inward-shares',
      title: 'Inward Document Shares',
      subtitle: `${summary.sharedFiles?.toLocaleString() || 0} Shared Files`,
      desc: 'Direct File Grants',
      icon: '📑',
      color: '#8B5CF6',
      bg: '#F5F3FF',
      border: '#DDD6FE',
      action: () => onDrillDown({ tab: 'files', filterType: 'SHARED' }),
    },
  ];

  // 2. Right Wing: Outward Effective Entitlements & Privileges
  const rightEntitlements = [
    {
      id: 'ent-full-control',
      title: 'Full Control (Owner)',
      subtitle: `${summary.ownedFiles?.toLocaleString() || 0} Documents`,
      desc: 'Read, Write, Delete & Share',
      icon: '👑',
      color: '#10B981',
      bg: '#ECFDF5',
      border: '#A7F3D0',
      action: () => onDrillDown({ tab: 'files', roleFilter: 'Owner' }),
    },
    {
      id: 'ent-collaborator',
      title: 'Collaborator (Edit)',
      subtitle: `${files.filter(f => f.role?.includes('Write') || f.role?.includes('Edit')).length} Documents`,
      desc: 'Can Modify & Save',
      icon: '✍️',
      color: '#3B82F6',
      bg: '#EFF6FF',
      border: '#BFDBFE',
      action: () => onDrillDown({ tab: 'files', roleFilter: 'Editor' }),
    },
    {
      id: 'ent-viewer',
      title: 'Viewer (Read-Only)',
      subtitle: `${files.filter(f => f.role?.includes('View') || f.role?.includes('Read')).length || summary.sharedFiles || 0} Documents`,
      desc: 'Inspect & Download Only',
      icon: '👁️',
      color: '#F59E0B',
      bg: '#FFFBEB',
      border: '#FDE68A',
      action: () => onDrillDown({ tab: 'files', roleFilter: 'Viewer' }),
    },
    {
      id: 'ent-sharing-links',
      title: 'Active Sharing Links',
      subtitle: `${files.filter(f => f.sharingLinksCount > 0).length} Exposed URLs`,
      desc: 'External/Anonymous Access',
      icon: '🔗',
      color: '#EF4444',
      bg: '#FEF2F2',
      border: '#FECACA',
      action: () => onDrillDown({ tab: 'files', riskFilter: 'High' }),
    },
  ];

  return (
    <div
      style={{
        position: 'relative',
        background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
        border: '1.5px solid #E2E8F0',
        borderRadius: '24px',
        padding: '36px 40px',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.03)',
        overflow: 'hidden',
        minHeight: '480px',
      }}
    >
      {/* Visual Instruction Banner */}
      <div style={{ textAlign: 'center', marginBottom: '20px', fontSize: '13px', color: '#64748B', fontWeight: '600' }}>
        💡 <strong style={{ color: '#4F46E5' }}>Interactive Model:</strong> Click any vector card on the left or right to immediately drill down into its filtered documents!
      </div>

      {/* SVG Curved Flow Lines connecting Left ➔ Center ➔ Right */}
      <svg
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
      >
        {/* Left lines (Sources to Center) */}
        {leftSources.map((_, idx) => {
          const y1 = 20 + idx * 20;
          return (
            <path
              key={`left-flow-${idx}`}
              d={`M 28 ${y1} Q 40 ${y1}, 46 52`}
              fill="none"
              stroke="#CBD5E1"
              strokeWidth="0.8"
              strokeDasharray="2.5,2.5"
            />
          );
        })}

        {/* Right lines (Center to Entitlements) */}
        {rightEntitlements.map((_, idx) => {
          const y2 = 20 + idx * 20;
          return (
            <path
              key={`right-flow-${idx}`}
              d={`M 54 52 Q 60 ${y2}, 72 ${y2}`}
              fill="none"
              stroke="#CBD5E1"
              strokeWidth="0.8"
              strokeDasharray="2.5,2.5"
            />
          );
        })}
      </svg>

      <div style={{ position: 'relative', zIndex: 2, display: 'grid', gridTemplateColumns: '1.15fr 1fr 1.15fr', gap: '36px', alignItems: 'center' }}>
        
        {/* ── LEFT WING: INWARD ACCESS SOURCES ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748B', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#3B82F6' }} />
            Inward Access Sources (Origins)
          </div>

          {leftSources.map((src, idx) => (
            <div
              key={`src-${idx}`}
              onClick={src.action}
              style={{
                background: '#FFFFFF',
                border: `1.5px solid ${src.border}`,
                borderRadius: '16px',
                padding: '16px 20px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(6px)'; e.currentTarget.style.borderColor = '#4F46E5'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.06)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateX(0)'; e.currentTarget.style.borderColor = src.border; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.02)'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: src.bg,
                    color: src.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    flexShrink: 0,
                    border: `1px solid ${src.border}`,
                  }}
                >
                  {src.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: '800', fontSize: '14.5px', color: '#0F172A' }}>{src.title}</div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: src.color, marginTop: '2px' }}>{src.subtitle}</div>
                  <div style={{ fontSize: '11.5px', color: '#64748B' }}>{src.desc}</div>
                </div>
              </div>

              <span style={{ fontSize: '12px', fontWeight: '800', color: '#6366F1', background: '#EEF2FF', padding: '4px 8px', borderRadius: '8px' }}>
                Drill Down ➔
              </span>
            </div>
          ))}
        </div>

        {/* ── CENTER KNOT: USER CORE IDENTITY ── */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div
            style={{
              background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 100%)',
              border: '2px solid #4338CA',
              borderRadius: '24px',
              padding: '30px 24px',
              boxShadow: '0 16px 40px rgba(49, 46, 129, 0.35)',
              textAlign: 'center',
              color: '#FFFFFF',
              maxWidth: '320px',
              width: '100%',
              position: 'relative',
            }}
          >
            {/* Avatar */}
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '22px',
                background: 'linear-gradient(135deg, #FF7043 0%, #FF5722 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '26px',
                fontWeight: '900',
                margin: '0 auto 16px',
                boxShadow: '0 8px 24px rgba(255, 112, 67, 0.4)',
                border: '3px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              {user?.displayName?.substring(0, 2).toUpperCase() || 'U'}
            </div>

            <div style={{ fontWeight: '900', fontSize: '18px', color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.displayName || 'Corporate User'}
            </div>
            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.75)', marginTop: '2px', fontFamily: 'monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.email || user?.userPrincipalName}
            </div>

            <div style={{ margin: '16px 0', borderTop: '1px solid rgba(255, 255, 255, 0.15)', borderBottom: '1px solid rgba(255, 255, 255, 0.15)', padding: '12px 0', display: 'flex', justifyContent: 'space-around' }}>
              <div onClick={() => onDrillDown({ tab: 'folders' })} style={{ cursor: 'pointer' }} title="Click to view all folders">
                <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.7)', fontWeight: '700', textTransform: 'uppercase' }}>Folders ➔</div>
                <div style={{ fontSize: '20px', fontWeight: '900', color: '#93C5FD' }}>{summary.totalAccessibleFolders || 0}</div>
              </div>
              <div style={{ borderLeft: '1px solid rgba(255, 255, 255, 0.15)' }} />
              <div onClick={() => onDrillDown({ tab: 'files' })} style={{ cursor: 'pointer' }} title="Click to view all files">
                <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.7)', fontWeight: '700', textTransform: 'uppercase' }}>Files ➔</div>
                <div style={{ fontSize: '20px', fontWeight: '900', color: '#6EE7B7' }}>{summary.totalAccessibleFiles?.toLocaleString() || 0}</div>
              </div>
            </div>

            <span style={{ fontSize: '11px', background: 'rgba(16, 185, 129, 0.25)', color: '#A7F3D0', padding: '4px 12px', borderRadius: '20px', fontWeight: '800', border: '1px solid rgba(16, 185, 129, 0.4)' }}>
              🟢 M365 Active Account
            </span>
          </div>
        </div>

        {/* ── RIGHT WING: EFFECTIVE ENTITLEMENTS & EXPOSURES ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748B', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10B981' }} />
            Effective Rights & Exposures (Outcomes)
          </div>

          {rightEntitlements.map((ent, idx) => (
            <div
              key={`ent-${idx}`}
              onClick={ent.action}
              style={{
                background: '#FFFFFF',
                border: `1.5px solid ${ent.border}`,
                borderRadius: '16px',
                padding: '16px 20px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateX(-6px)'; e.currentTarget.style.borderColor = '#4F46E5'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.06)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateX(0)'; e.currentTarget.style.borderColor = ent.border; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.02)'; }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '12px',
                    background: ent.bg,
                    color: ent.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '20px',
                    flexShrink: 0,
                    border: `1px solid ${ent.border}`,
                  }}
                >
                  {ent.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: '800', fontSize: '14.5px', color: '#0F172A' }}>{ent.title}</div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: ent.color, marginTop: '2px' }}>{ent.subtitle}</div>
                  <div style={{ fontSize: '11.5px', color: '#64748B' }}>{ent.desc}</div>
                </div>
              </div>

              <span style={{ fontSize: '12px', fontWeight: '800', color: '#6366F1', background: '#EEF2FF', padding: '4px 8px', borderRadius: '8px' }}>
                Drill Down ➔
              </span>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}

/* ── MAIN USER ACCESS EXPLORER PAGE ── */
export default function UserAccessExplorerPage() {
  const [usersList, setUsersList] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearchText, setUserSearchText] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState('ALL');
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Access Data State
  const [accessData, setAccessData] = useState(null);
  const [loadingAccess, setLoadingAccess] = useState(false);

  // Active View Tab: 'bowtie', 'folders', or 'files'
  const [activeTab, setActiveTab] = useState('bowtie');

  // Breadcrumb / Drill-Down State
  const [activeDrillDownFolder, setActiveDrillDownFolder] = useState(null);

  // Folders Filter & Pagination
  const [folderSearch, setFolderSearch] = useState('');
  const [folderCurrentPage, setFolderCurrentPage] = useState(1);
  const folderItemsPerPage = 10;

  // Files Search & Server-Side Pagination
  const [fileSearch, setFileSearch] = useState('');
  const [fileFolderFilter, setFileFolderFilter] = useState('All');
  const [fileTypeFilter, setFileTypeFilter] = useState('ALL');
  const [fileRoleFilter, setFileRoleFilter] = useState('ALL');
  const [fileCurrentPage, setFileCurrentPage] = useState(1);
  const fileItemsPerPage = 10;

  // 1. Load users list on mount
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await api.get('/governance/users');
        const sorted = (res.data || []).sort((a, b) => (b.totalFilesCount || 0) - (a.totalFilesCount || 0));
        setUsersList(sorted);
        if (sorted.length > 0) {
          setSelectedUserId(sorted[0].id);
        }
      } catch (err) {
        console.error(err);
        toast.error('Failed to load corporate employees list');
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  // 2. Fetch user's access data when selected user or file filters change
  const fetchUserAccess = async (userId, page = 1, search = '', folderId = '') => {
    if (!userId) return;
    setLoadingAccess(true);
    try {
      const res = await api.get(`/governance/user-access/${userId}`, {
        params: {
          page,
          limit: fileItemsPerPage,
          search: search.trim(),
          folderId: folderId !== 'All' ? folderId : '',
        },
      });
      setAccessData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to resolve effective access matrix for user');
    } finally {
      setLoadingAccess(false);
    }
  };

  useEffect(() => {
    if (selectedUserId) {
      fetchUserAccess(selectedUserId, fileCurrentPage, fileSearch, fileFolderFilter);
    }
  }, [selectedUserId, fileCurrentPage, fileSearch, fileFolderFilter]);

  // Handle user change
  const handleSelectUser = (userId) => {
    setSelectedUserId(userId);
    setIsDropdownOpen(false);
    setUserSearchText('');
    setFileCurrentPage(1);
    setFolderCurrentPage(1);
    setFileSearch('');
    setFolderSearch('');
    setFileFolderFilter('All');
    setFileTypeFilter('ALL');
    setFileRoleFilter('ALL');
    setActiveDrillDownFolder(null);
  };

  // Generic Drill Down Handler from Bowtie or Cards
  const handleDrillDown = ({ tab, filterType, roleFilter, folderId, folderName, riskFilter }) => {
    if (tab === 'folders') {
      setActiveTab('folders');
      if (filterType === 'SHARED') setFolderSearch('Shared');
      else setFolderSearch('');
    } else if (tab === 'files') {
      setActiveTab('files');
      if (folderId) {
        setFileFolderFilter(folderId);
        setActiveDrillDownFolder(folderName || 'Selected Folder');
      } else {
        setActiveDrillDownFolder(null);
      }
      if (roleFilter) setFileRoleFilter(roleFilter);
      if (riskFilter) setFileRoleFilter('Risk');
    }
    setFileCurrentPage(1);
    setFolderCurrentPage(1);
  };

  // Filtered users for dropdown search & category tab
  const filteredUsers = useMemo(() => {
    return usersList.filter((u) => {
      const matchesSearch =
        u.displayName.toLowerCase().includes(userSearchText.toLowerCase()) ||
        u.email.toLowerCase().includes(userSearchText.toLowerCase()) ||
        (u.department && u.department.toLowerCase().includes(userSearchText.toLowerCase()));

      if (!matchesSearch) return false;
      if (departmentFilter === 'BRANCHES') return u.displayName.toLowerCase().includes('kaizen') || u.displayName.toLowerCase().includes('branch') || u.department.toLowerCase().includes('branch');
      if (departmentFilter === 'CORPORATE') return !u.displayName.toLowerCase().includes('kaizen');
      return true;
    });
  }, [usersList, userSearchText, departmentFilter]);

  const selectedUser = usersList.find((u) => u.id === selectedUserId) || accessData?.user;

  // Filtered Folders list with pagination (20 per page)
  const accessibleFolders = useMemo(() => {
    return (accessData?.folders || []).filter((f) =>
      f.name.toLowerCase().includes(folderSearch.toLowerCase())
    );
  }, [accessData?.folders, folderSearch]);

  const folderTotalPages = Math.ceil(accessibleFolders.length / folderItemsPerPage) || 1;
  const paginatedFolders = useMemo(() => {
    return accessibleFolders.slice(
      (folderCurrentPage - 1) * folderItemsPerPage,
      folderCurrentPage * folderItemsPerPage
    );
  }, [accessibleFolders, folderCurrentPage, folderItemsPerPage]);

  // Filtered Files by Type and Role
  const displayedFiles = useMemo(() => {
    let raw = accessData?.files || [];
    if (fileTypeFilter !== 'ALL') {
      raw = raw.filter((f) => f.fileType?.toUpperCase() === fileTypeFilter);
    }
    if (fileRoleFilter === 'Owner') {
      raw = raw.filter((f) => f.accessType === 'Owner');
    } else if (fileRoleFilter === 'Editor') {
      raw = raw.filter((f) => f.role?.includes('Write') || f.role?.includes('Edit'));
    } else if (fileRoleFilter === 'Viewer') {
      raw = raw.filter((f) => f.role?.includes('View') || f.role?.includes('Read'));
    } else if (fileRoleFilter === 'Risk') {
      raw = raw.filter((f) => f.sharingLinksCount > 0);
    }
    return raw;
  }, [accessData?.files, fileTypeFilter, fileRoleFilter]);

  const summary = accessData?.summary || {
    totalAccessibleFiles: 0,
    ownedFiles: 0,
    sharedFiles: 0,
    totalAccessibleFolders: 0,
  };

  // Input styles
  const searchInputStyle = {
    background: '#FFFFFF',
    border: '1.5px solid #CBD5E1',
    borderRadius: '12px',
    padding: '12px 18px',
    color: '#0F172A',
    fontSize: '14.5px',
    fontWeight: '500',
    outline: 'none',
    width: '100%',
    height: '48px',
  };

  return (
    <div style={{ padding: '36px 44px', maxWidth: '1800px', margin: '0 auto', minHeight: '100vh', background: 'transparent' }}>
      
      {/* ── 1. HEADER SECTION ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 24, marginBottom: 28 }}>
        <div>
          <div style={{ fontSize: '32px', fontWeight: '900', letterSpacing: '-0.03em', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <span style={{
              background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
              width: '46px',
              height: '46px',
              borderRadius: '14px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '22px',
              color: 'white',
              boxShadow: '0 6px 18px rgba(99, 102, 241, 0.35)',
            }}>
              👤
            </span>
            User Access Explorer
          </div>
          <div style={{ color: '#64748B', fontSize: '15.5px', marginTop: 6, fontWeight: '500', maxWidth: '780px', lineHeight: 1.5 }}>
            Inspect effective folder and document entitlements, interactive bowtie access models, and sharing exposures across all 110,000+ files for any corporate employee.
          </div>
        </div>

        {selectedUserId && (
          <a
            href={`${API_URL}/governance/user-access/${selectedUserId}/export`}
            download
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 24px',
              borderRadius: '14px',
              fontWeight: '700',
              fontSize: '14px',
              background: '#FFFFFF',
              border: '1.5px solid #CBD5E1',
              color: '#1E293B',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.04)',
              textDecoration: 'none',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366F1'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#CBD5E1'; e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2.2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export User Audit (Excel)
          </a>
        )}
      </div>

      {/* ── 2. EMPLOYEE SELECTOR & IDENTITY HERO CARD ────────────────────────── */}
      <div
        style={{
          background: 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
          border: '1.5px solid #E2E8F0',
          borderRadius: '24px',
          padding: '28px 34px',
          marginBottom: '30px',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.03)',
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.1fr', gap: '32px', alignItems: 'center' }}>
          
          {/* User Combobox */}
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <label style={{ fontSize: '12.5px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#475569' }}>
                Select Corporate Employee ({usersList.length} Accounts)
              </label>

              <div style={{ display: 'flex', gap: '6px', background: '#F1F5F9', padding: '3px', borderRadius: '10px' }}>
                {['ALL', 'CORPORATE', 'BRANCHES'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setDepartmentFilter(tab)}
                    style={{
                      border: 'none',
                      background: departmentFilter === tab ? '#FFFFFF' : 'transparent',
                      color: departmentFilter === tab ? '#4F46E5' : '#64748B',
                      fontSize: '11px',
                      fontWeight: '800',
                      padding: '4px 10px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      boxShadow: departmentFilter === tab ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search employee by name, department, or email..."
                style={{
                  ...searchInputStyle,
                  paddingLeft: '48px',
                  borderColor: isDropdownOpen ? '#6366F1' : '#CBD5E1',
                }}
                value={userSearchText || (isDropdownOpen ? '' : (selectedUser?.displayName ? `${selectedUser.displayName} (${selectedUser.email})` : ''))}
                onChange={(e) => {
                  setUserSearchText(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
              />
              <span style={{ position: 'absolute', left: '16px', top: '14px', fontSize: '18px', color: '#94A3B8' }}>
                🔍
              </span>
              {isDropdownOpen && (
                <button
                  onClick={() => setIsDropdownOpen(false)}
                  style={{ position: 'absolute', right: '14px', top: '13px', background: '#F1F5F9', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', color: '#64748B' }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Dropdown Options Popup */}
            {isDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  marginTop: '8px',
                  background: '#FFFFFF',
                  border: '1.5px solid #CBD5E1',
                  borderRadius: '16px',
                  boxShadow: '0 12px 36px rgba(0,0,0,0.12)',
                  maxHeight: '340px',
                  overflowY: 'auto',
                  zIndex: 999,
                  padding: '8px',
                }}
              >
                {filteredUsers.map((u) => (
                  <div
                    key={u.id}
                    onClick={() => handleSelectUser(u.id)}
                    style={{
                      padding: '12px 16px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: u.id === selectedUserId ? '#EEF2FF' : 'transparent',
                      marginBottom: '2px',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#F8FAFC'}
                    onMouseLeave={(e) => e.currentTarget.style.background = u.id === selectedUserId ? '#EEF2FF' : 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#6366F1', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '800' }}>
                        {u.displayName.substring(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '14.5px', color: '#1E293B' }}>{u.displayName}</div>
                        <div style={{ fontSize: '12px', color: '#64748B' }}>{u.email}</div>
                      </div>
                    </div>
                    <span style={{ fontSize: '11.5px', background: '#F1F5F9', color: '#475569', padding: '4px 10px', borderRadius: '8px', fontWeight: '700' }}>
                      {u.totalFilesCount ? `${u.totalFilesCount.toLocaleString()} files` : '0 files'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right: Active Member Identity Banner */}
          {selectedUser && (
            <div
              style={{
                background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 100%)',
                padding: '22px 26px',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '20px',
                boxShadow: '0 8px 24px rgba(49, 46, 129, 0.3)',
                color: '#FFFFFF',
              }}
            >
              <div
                style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '18px',
                  background: 'linear-gradient(135deg, #FF7043 0%, #FF5722 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '22px',
                  fontWeight: '900',
                  boxShadow: '0 6px 16px rgba(255, 112, 67, 0.35)',
                  flexShrink: 0,
                  border: '2px solid rgba(255, 255, 255, 0.25)',
                }}
              >
                {selectedUser.displayName?.substring(0, 2).toUpperCase() || 'U'}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: '900', fontSize: '18px', color: '#FFFFFF', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {selectedUser.displayName}
                </div>
                <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.75)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'monospace' }}>
                  {selectedUser.email}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '11px', background: 'rgba(16, 185, 129, 0.25)', color: '#A7F3D0', padding: '3px 10px', borderRadius: '8px', fontWeight: '800', border: '1px solid rgba(16, 185, 129, 0.4)' }}>
                    🟢 M365 Active Principal
                  </span>
                  <span style={{ fontSize: '11px', background: 'rgba(255, 255, 255, 0.15)', color: '#FFFFFF', padding: '3px 10px', borderRadius: '8px', fontWeight: '800', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    🏢 {selectedUser.department || 'Kaizen Corporate'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── 3. METRICS CARDS (CLICKABLE WITH INTERACTIVE DRILL-DOWN) ─────────── */}
      {accessData?.summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '22px', marginBottom: '30px' }}>
          
          <div
            onClick={() => handleDrillDown({ tab: 'folders' })}
            style={{
              background: '#FFFFFF',
              border: '1.5px solid #DBEAFE',
              borderRadius: '20px',
              padding: '24px 28px',
              boxShadow: '0 4px 16px rgba(59, 130, 246, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(59, 130, 246, 0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.05)'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
              <div style={{ width: '54px', height: '54px', borderRadius: '16px', background: '#EFF6FF', color: '#2563EB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', flexShrink: 0, border: '1px solid #BFDBFE' }}>
                📁
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Accessible Folders</div>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#0F172A', marginTop: '4px' }}>
                  {summary.totalAccessibleFolders.toLocaleString()}
                </div>
                <div style={{ fontSize: '12px', color: '#2563EB', fontWeight: '700', marginTop: '2px' }}>OneDrive & Libraries ➔</div>
              </div>
            </div>
          </div>

          <div
            onClick={() => handleDrillDown({ tab: 'files' })}
            style={{
              background: '#FFFFFF',
              border: '1.5px solid #D1FAE5',
              borderRadius: '20px',
              padding: '24px 28px',
              boxShadow: '0 4px 16px rgba(16, 185, 129, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(16, 185, 129, 0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.05)'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
              <div style={{ width: '54px', height: '54px', borderRadius: '16px', background: '#ECFDF5', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', flexShrink: 0, border: '1px solid #A7F3D0' }}>
                📄
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Accessible Files</div>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#0F172A', marginTop: '4px' }}>
                  {summary.totalAccessibleFiles.toLocaleString()}
                </div>
                <div style={{ fontSize: '12px', color: '#059669', fontWeight: '700', marginTop: '2px' }}>Active Document List ➔</div>
              </div>
            </div>
          </div>

          <div
            onClick={() => handleDrillDown({ tab: 'files', roleFilter: 'Owner' })}
            style={{
              background: '#FFFFFF',
              border: '1.5px solid #FFEDD5',
              borderRadius: '20px',
              padding: '24px 28px',
              boxShadow: '0 4px 16px rgba(255, 112, 67, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(255, 112, 67, 0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(255, 112, 67, 0.05)'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
              <div style={{ width: '54px', height: '54px', borderRadius: '16px', background: '#FFF7ED', color: '#FF7043', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', flexShrink: 0, border: '1px solid #FED7AA' }}>
                👑
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Directly Owned Assets</div>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#0F172A', marginTop: '4px' }}>
                  {summary.ownedFiles.toLocaleString()}
                </div>
                <div style={{ fontSize: '12px', color: '#C2410C', fontWeight: '700', marginTop: '2px' }}>Personal OneDrive Root ➔</div>
              </div>
            </div>
          </div>

          <div
            onClick={() => handleDrillDown({ tab: 'files', roleFilter: 'Viewer' })}
            style={{
              background: '#FFFFFF',
              border: '1.5px solid #EDE9FE',
              borderRadius: '20px',
              padding: '24px 28px',
              boxShadow: '0 4px 16px rgba(124, 58, 237, 0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(124, 58, 237, 0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(124, 58, 237, 0.05)'; }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
              <div style={{ width: '54px', height: '54px', borderRadius: '16px', background: '#F5F3FF', color: '#7C3AED', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', flexShrink: 0, border: '1px solid #DDD6FE' }}>
                👥
              </div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Shared by Colleagues</div>
                <div style={{ fontSize: '28px', fontWeight: '900', color: '#0F172A', marginTop: '4px' }}>
                  {summary.sharedFiles.toLocaleString()}
                </div>
                <div style={{ fontSize: '12px', color: '#7C3AED', fontWeight: '700', marginTop: '2px' }}>Inward Team Shares ➔</div>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* ── 4. MAIN WORKSPACE CONTAINER & SWITCHABLE TABS ─────────────────────── */}
      <div
        style={{
          background: '#FFFFFF',
          border: '1.5px solid #E2E8F0',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.03)',
        }}
      >
        {/* TAB SWITCHER HEADER */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <h3 style={{ fontSize: '22px', fontWeight: '900', color: '#0F172A', margin: 0, letterSpacing: '-0.02em' }}>
              Entitlements & Rights Workspace
            </h3>
            <div style={{ fontSize: '14px', color: '#64748B', marginTop: 4, fontWeight: '500' }}>
              Inspect access models and documents for <strong style={{ color: '#0F172A' }}>{selectedUser?.displayName}</strong>
            </div>
          </div>

          {/* 3 SWITCHABLE TABS: BOWTIE, FOLDERS, FILES */}
          <div style={{ display: 'flex', gap: '8px', background: '#F1F5F9', padding: '6px', borderRadius: '16px' }}>
            <button
              style={{
                padding: '10px 20px',
                fontSize: '13.5px',
                borderRadius: '12px',
                border: 'none',
                background: activeTab === 'bowtie' ? '#FFFFFF' : 'transparent',
                color: activeTab === 'bowtie' ? '#4F46E5' : '#64748B',
                fontWeight: '800',
                boxShadow: activeTab === 'bowtie' ? '0 4px 12px rgba(0,0,0,0.06)' : 'none',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onClick={() => setActiveTab('bowtie')}
            >
              <span>🎀</span> Bowtie Access Model
            </button>
            <button
              style={{
                padding: '10px 20px',
                fontSize: '13.5px',
                borderRadius: '12px',
                border: 'none',
                background: activeTab === 'folders' ? '#FFFFFF' : 'transparent',
                color: activeTab === 'folders' ? '#0F172A' : '#64748B',
                fontWeight: '800',
                boxShadow: activeTab === 'folders' ? '0 4px 12px rgba(0,0,0,0.06)' : 'none',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onClick={() => setActiveTab('folders')}
            >
              <span>📁</span> Accessible Folders ({accessData?.folders?.length || 0})
            </button>
            <button
              style={{
                padding: '10px 20px',
                fontSize: '13.5px',
                borderRadius: '12px',
                border: 'none',
                background: activeTab === 'files' ? '#FFFFFF' : 'transparent',
                color: activeTab === 'files' ? '#0F172A' : '#64748B',
                fontWeight: '800',
                boxShadow: activeTab === 'files' ? '0 4px 12px rgba(0,0,0,0.06)' : 'none',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onClick={() => setActiveTab('files')}
            >
              <span>📄</span> Accessible Documents ({summary.totalAccessibleFiles ? summary.totalAccessibleFiles.toLocaleString() : 0})
            </button>
          </div>
        </div>

        {/* ── TAB 1: BOWTIE ACCESS DIAGRAM ── */}
        {activeTab === 'bowtie' && (
          <UserBowTieDiagram
            user={selectedUser}
            summary={summary}
            folders={accessData?.folders || []}
            files={accessData?.files || []}
            loadingAccess={loadingAccess}
            onDrillDown={handleDrillDown}
          />
        )}

        {/* ── TAB 2: ACCESSIBLE FOLDERS TABLE (20 PER PAGE) ── */}
        {activeTab === 'folders' && (
          <div>
            <div style={{ marginBottom: '20px', maxWidth: '400px' }}>
              <input
                type="text"
                placeholder="Search accessible folders by name..."
                style={searchInputStyle}
                value={folderSearch}
                onChange={(e) => {
                  setFolderSearch(e.target.value);
                  setFolderCurrentPage(1);
                }}
              />
            </div>

            {loadingAccess ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div className="spinner" style={{ margin: '0 auto 12px' }} />
                <div style={{ fontSize: '14px', color: '#64748B' }}>Loading accessible folders...</div>
              </div>
            ) : accessibleFolders.length === 0 ? (
              <div style={{ padding: '60px 0', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', marginBottom: '10px' }}>📁</div>
                <div style={{ color: '#0F172A', fontSize: '16px', fontWeight: '800' }}>No accessible folders found</div>
                <div style={{ color: '#64748B', fontSize: '13px', marginTop: '4px' }}>This employee has no direct folders mapped.</div>
              </div>
            ) : (
              <div>
                <div style={{ overflowX: 'auto', borderRadius: '16px', border: '1.5px solid #F1F5F9' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', background: '#FFFFFF' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #E2E8F0', background: '#F8FAFC', color: '#475569', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        <th style={{ padding: '18px 24px' }}>Folder Name</th>
                        <th style={{ padding: '18px 24px' }}>Access Source</th>
                        <th style={{ padding: '18px 24px' }}>Permission Level</th>
                        <th style={{ padding: '18px 24px' }}>Drive / Library</th>
                        <th style={{ padding: '18px 24px', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedFolders.map((folder) => (
                        <tr key={folder.id} style={{ borderBottom: '1px solid #F1F5F9', fontSize: '14.5px' }} className="table-row-hover">
                          <td style={{ padding: '20px 24px', fontWeight: '800', color: '#0F172A' }}>
                            <div
                              onClick={() => handleDrillDown({ tab: 'files', folderId: folder.id, folderName: folder.name })}
                              style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#1E293B' }}
                              title="Click to explore files in this folder"
                            >
                              <span style={{ fontSize: '20px' }}>📁</span>
                              <span style={{ textDecoration: 'underline', textDecorationColor: '#CBD5E1' }}>{folder.name}</span>
                            </div>
                          </td>
                          <td style={{ padding: '20px 24px' }}>
                            <span
                              style={{
                                padding: '6px 14px',
                                borderRadius: '24px',
                                fontSize: '12px',
                                fontWeight: '800',
                                background: folder.accessType.includes('Owner') ? '#DCFCE7' : '#EFF6FF',
                                color: folder.accessType.includes('Owner') ? '#166534' : '#1D4ED8',
                                border: folder.accessType.includes('Owner') ? '1px solid #BBF7D0' : '1px solid #DBEAFE',
                              }}
                            >
                              {folder.accessType}
                            </span>
                          </td>
                          <td style={{ padding: '20px 24px', fontWeight: '700', color: '#334155' }}>
                            {folder.role}
                          </td>
                          <td style={{ padding: '20px 24px', color: '#64748B', fontSize: '14px', fontWeight: '500' }}>
                            {folder.driveName}
                          </td>
                          <td style={{ padding: '20px 24px', textAlign: 'right' }}>
                            <div style={{ display: 'inline-flex', gap: '8px' }}>
                              <button
                                onClick={() => handleDrillDown({ tab: 'files', folderId: folder.id, folderName: folder.name })}
                                style={{
                                  background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)',
                                  color: '#FFFFFF',
                                  borderRadius: '10px',
                                  padding: '8px 14px',
                                  fontSize: '12.5px',
                                  fontWeight: '700',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                }}
                              >
                                📄 View Files ➔
                              </button>
                              <a
                                href={folder.webUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  background: '#EFF6FF',
                                  color: '#2563EB',
                                  borderRadius: '10px',
                                  padding: '8px 14px',
                                  fontSize: '12.5px',
                                  fontWeight: '700',
                                  textDecoration: 'none',
                                  border: '1px solid #BFDBFE',
                                }}
                              >
                                Open in OneDrive ↗
                              </a>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 20 RECORDS FOLDERS PAGINATION BAR */}
                {accessibleFolders.length > folderItemsPerPage && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', padding: '0 8px', flexWrap: 'wrap', gap: '16px' }}>
                    <div style={{ fontSize: '14px', color: '#475569', fontWeight: '600' }}>
                      Showing {(folderCurrentPage - 1) * folderItemsPerPage + 1} to {Math.min(folderCurrentPage * folderItemsPerPage, accessibleFolders.length)} of <strong style={{ color: '#0F172A' }}>{accessibleFolders.length}</strong> accessible folders
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                        disabled={folderCurrentPage === 1}
                        onClick={() => setFolderCurrentPage(1)}
                      >
                        ⏮ First
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                        disabled={folderCurrentPage === 1}
                        onClick={() => setFolderCurrentPage((prev) => Math.max(1, prev - 1))}
                      >
                        ◀ Prev
                      </button>
                      <span style={{ fontSize: '13.5px', fontWeight: '800', color: '#0F172A', padding: '0 10px' }}>
                        Page {folderCurrentPage} of {folderTotalPages}
                      </span>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                        disabled={folderCurrentPage >= folderTotalPages}
                        onClick={() => setFolderCurrentPage((prev) => Math.min(folderTotalPages, prev + 1))}
                      >
                        Next ▶
                      </button>
                      <button
                        className="btn btn-secondary"
                        style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                        disabled={folderCurrentPage >= folderTotalPages}
                        onClick={() => setFolderCurrentPage(folderTotalPages)}
                      >
                        Last ⏭
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── TAB 3: ACCESSIBLE DOCUMENTS TABLE (20 PER PAGE WITH BREADCRUMB) ── */}
        {activeTab === 'files' && (
          <div>
            {/* DRILL-DOWN BREADCRUMB BANNER */}
            {activeDrillDownFolder && (
              <div
                style={{
                  background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
                  border: '1.5px solid #C7D2FE',
                  borderRadius: '16px',
                  padding: '14px 20px',
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 12,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '20px' }}>📂</span>
                  <div>
                    <span style={{ fontSize: '12px', fontWeight: '800', color: '#4338CA', textTransform: 'uppercase' }}>Filtered by Folder Directory:</span>
                    <span style={{ fontSize: '15px', fontWeight: '900', color: '#1E1B4B', marginLeft: '6px' }}>{activeDrillDownFolder}</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setFileFolderFilter('All');
                    setActiveDrillDownFolder(null);
                    setFileRoleFilter('ALL');
                    setFileCurrentPage(1);
                  }}
                  style={{
                    background: '#FFFFFF',
                    border: '1px solid #C7D2FE',
                    borderRadius: '10px',
                    padding: '6px 14px',
                    fontSize: '12.5px',
                    fontWeight: '800',
                    color: '#4F46E5',
                    cursor: 'pointer',
                  }}
                >
                  ✕ Clear Filter (View All Files)
                </button>
              </div>
            )}

            {/* ROLE FILTER BANNER */}
            {fileRoleFilter !== 'ALL' && (
              <div
                style={{
                  background: '#FFF7ED',
                  border: '1.5px solid #FFEDD5',
                  borderRadius: '14px',
                  padding: '10px 18px',
                  marginBottom: '18px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#C2410C' }}>
                  🛡️ Active Role Filter: <strong>{fileRoleFilter}</strong> permissions
                </span>
                <button
                  onClick={() => setFileRoleFilter('ALL')}
                  style={{ background: 'white', border: '1px solid #FED7AA', borderRadius: '8px', padding: '4px 10px', fontSize: '12px', fontWeight: '800', color: '#C2410C', cursor: 'pointer' }}
                >
                  Show All Roles
                </button>
              </div>
            )}

            {/* SEARCH & FILTERS CONTROLS */}
            <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', flexWrap: 'wrap' }}>
              <div style={{ flex: 2, minWidth: '280px' }}>
                <input
                  type="text"
                  placeholder="Search user's documents by filename..."
                  style={searchInputStyle}
                  value={fileSearch}
                  onChange={(e) => {
                    setFileSearch(e.target.value);
                    setFileCurrentPage(1);
                  }}
                />
              </div>
              <div style={{ flex: 1.2, minWidth: '200px' }}>
                <select
                  style={{
                    ...searchInputStyle,
                    cursor: 'pointer',
                  }}
                  value={fileFolderFilter}
                  onChange={(e) => {
                    setFileFolderFilter(e.target.value);
                    setActiveDrillDownFolder(e.target.value === 'All' ? null : (accessData?.folders || []).find(f => f.id === e.target.value)?.name || 'Selected Folder');
                    setFileCurrentPage(1);
                  }}
                >
                  <option value="All">All Parent Folders</option>
                  {(accessData?.folders || []).map((f, idx) => (
                    <option key={idx} value={f.id}>
                      📁 {f.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ width: '160px' }}>
                <select
                  style={{
                    ...searchInputStyle,
                    cursor: 'pointer',
                  }}
                  value={fileTypeFilter}
                  onChange={(e) => setFileTypeFilter(e.target.value)}
                >
                  <option value="ALL">All Formats</option>
                  <option value="XLSX">Excel (.xlsx)</option>
                  <option value="PDF">PDF (.pdf)</option>
                  <option value="DOCX">Word (.docx)</option>
                </select>
              </div>
            </div>

            {loadingAccess ? (
              <div style={{ textAlign: 'center', padding: '60px 0' }}>
                <div className="spinner" style={{ margin: '0 auto 12px' }} />
                <div style={{ fontSize: '14px', color: '#64748B' }}>Loading accessible documents...</div>
              </div>
            ) : displayedFiles.length === 0 ? (
              <div style={{ padding: '60px 0', textAlign: 'center' }}>
                <div style={{ fontSize: '36px', marginBottom: '10px' }}>📄</div>
                <div style={{ color: '#0F172A', fontSize: '16px', fontWeight: '800' }}>No accessible documents found</div>
                <div style={{ color: '#64748B', fontSize: '13px', marginTop: '4px' }}>Try adjusting your search query, role filter, or parent directory.</div>
              </div>
            ) : (
              <div>
                <div style={{ overflowX: 'auto', borderRadius: '16px', border: '1.5px solid #F1F5F9' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', background: '#FFFFFF' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #E2E8F0', background: '#F8FAFC', color: '#475569', fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        <th style={{ padding: '18px 24px' }}>Document Details</th>
                        <th style={{ padding: '18px 24px' }}>Parent Directory</th>
                        <th style={{ padding: '18px 24px' }}>Access Source</th>
                        <th style={{ padding: '18px 24px' }}>Permission Level</th>
                        <th style={{ padding: '18px 24px' }}>Last Modified</th>
                        <th style={{ padding: '18px 24px', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedFiles.map((file) => (
                        <tr key={file.id} style={{ borderBottom: '1px solid #F1F5F9', fontSize: '14.5px' }} className="table-row-hover">
                          <td style={{ padding: '20px 24px', color: '#0F172A' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                              <span
                                style={{
                                  padding: '5px 10px',
                                  borderRadius: '8px',
                                  fontSize: '11px',
                                  fontWeight: '900',
                                  background: file.fileType === 'pdf' ? '#FEE2E2' : file.fileType === 'xlsx' || file.fileType === 'xls' ? '#DCFCE7' : '#EFF6FF',
                                  color: file.fileType === 'pdf' ? '#991B1B' : file.fileType === 'xlsx' || file.fileType === 'xls' ? '#166534' : '#1E40AF',
                                  border: file.fileType === 'pdf' ? '1px solid #FECACA' : file.fileType === 'xlsx' || file.fileType === 'xls' ? '1px solid #BBF7D0' : '1px solid #DBEAFE',
                                  flexShrink: 0,
                                }}
                              >
                                {file.fileType ? file.fileType.toUpperCase() : 'FILE'}
                              </span>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: '800', fontSize: '15px', color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '400px' }} title={file.name}>
                                  {file.name}
                                </div>
                                <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>
                                  {file.sizeBytes ? `${(Number(file.sizeBytes) / 1024).toFixed(0)} KB` : '15 KB'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '20px 24px' }}>
                            <span style={{ fontSize: '13px', background: '#FFF7ED', color: '#C2410C', padding: '5px 12px', borderRadius: '8px', fontWeight: '700', border: '1px solid #FFEDD5' }}>
                              📁 /{file.parentFolderName}
                            </span>
                          </td>
                          <td style={{ padding: '20px 24px' }}>
                            <span
                              style={{
                                padding: '6px 14px',
                                borderRadius: '24px',
                                fontSize: '12px',
                                fontWeight: '800',
                                background: file.accessType === 'Owner' ? '#DCFCE7' : '#EFF6FF',
                                color: file.accessType === 'Owner' ? '#166534' : '#1E40AF',
                                border: file.accessType === 'Owner' ? '1px solid #BBF7D0' : '1px solid #DBEAFE',
                              }}
                            >
                              {file.accessType === 'Owner' ? '👑 Owner' : '👥 Shared'}
                            </span>
                          </td>
                          <td style={{ padding: '20px 24px', fontWeight: '700', color: '#334155', fontSize: '14px' }}>
                            {file.role}
                          </td>
                          <td style={{ padding: '20px 24px', color: '#64748B', fontSize: '13.5px', fontWeight: '500' }}>
                            {new Date(file.lastModified).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '20px 24px', textAlign: 'right' }}>
                            <a
                              href={file.webUrl}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                background: '#EFF6FF',
                                color: '#2563EB',
                                borderRadius: '10px',
                                padding: '8px 16px',
                                fontSize: '13px',
                                fontWeight: '700',
                                textDecoration: 'none',
                                border: '1px solid #BFDBFE',
                              }}
                            >
                              Open ↗
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 20 RECORDS FILES PAGINATION BAR */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', padding: '0 8px', flexWrap: 'wrap', gap: '16px' }}>
                  <div style={{ fontSize: '14px', color: '#475569', fontWeight: '600' }}>
                    Showing {(fileCurrentPage - 1) * fileItemsPerPage + 1} to {Math.min(fileCurrentPage * fileItemsPerPage, accessData.pagination.total)} of <strong style={{ color: '#0F172A' }}>{accessData.pagination.total.toLocaleString()}</strong> accessible files
                  </div>
                  
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                      disabled={fileCurrentPage === 1 || loadingAccess}
                      onClick={() => setFileCurrentPage(1)}
                    >
                      ⏮ First
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                      disabled={fileCurrentPage === 1 || loadingAccess}
                      onClick={() => setFileCurrentPage((prev) => Math.max(1, prev - 1))}
                    >
                      ◀ Prev
                    </button>
                    <span style={{ fontSize: '13.5px', fontWeight: '800', color: '#0F172A', padding: '0 10px' }}>
                      Page {fileCurrentPage} of {accessData.pagination.totalPages}
                    </span>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                      disabled={fileCurrentPage >= accessData.pagination.totalPages || loadingAccess}
                      onClick={() => setFileCurrentPage((prev) => Math.min(accessData.pagination.totalPages, prev + 1))}
                    >
                      Next ▶
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '6px 14px', fontSize: '12.5px', fontWeight: '700', borderRadius: '8px', background: 'white' }}
                      disabled={fileCurrentPage >= accessData.pagination.totalPages || loadingAccess}
                      onClick={() => setFileCurrentPage(accessData.pagination.totalPages)}
                    >
                      Last ⏭
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

      </div>

    </div>
  );
}
